import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatCLP, formatUSD } from "../../lib/format";

const emptyBundle = {
    name: "", slug: "", short_description: "", full_description: "",
    product_ids: [], price_clp: 0, price_usd: 0,
    image_url: "", status: "published", badge_text: "SALE",
    sale_mode: "one_time",
    sub_prices: {
        monthly: { clp: 0, usd: 0 },
        semiannual: { clp: 0, usd: 0 },
        annual: { clp: 0, usd: 0 },
    },
};

const SUB_PERIODS = [
    { key: "monthly", label: "Mensual" },
    { key: "semiannual", label: "Semestral (6 meses)" },
    { key: "annual", label: "Anual" },
];

export default function AdminBundles() {
    const [bundles, setBundles] = useState([]);
    const [products, setProducts] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyBundle);

    const load = async () => {
        const [b, p] = await Promise.all([
            api.get("/admin/bundles"),
            api.get("/admin/products"),
        ]);
        setBundles(b.data || []);
        setProducts(p.data || []);
    };
    useEffect(() => { load(); }, []);

    const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    const openCreate = () => {
        setEditing(null);
        setForm(emptyBundle);
        setOpen(true);
    };

    const openEdit = (b) => {
        setEditing(b);
        setForm({
            name: b.name || "",
            slug: b.slug || "",
            short_description: b.short_description || "",
            full_description: b.full_description || "",
            product_ids: b.product_ids || [],
            price_clp: b.price_clp || 0,
            price_usd: b.price_usd || 0,
            image_url: b.image_url || "",
            status: b.status || "published",
            badge_text: b.badge_text || "",
            sale_mode: b.sale_mode || "one_time",
            sub_prices: {
                monthly: { clp: b.sub_prices?.monthly?.clp || 0, usd: b.sub_prices?.monthly?.usd || 0 },
                semiannual: { clp: b.sub_prices?.semiannual?.clp || 0, usd: b.sub_prices?.semiannual?.usd || 0 },
                annual: { clp: b.sub_prices?.annual?.clp || 0, usd: b.sub_prices?.annual?.usd || 0 },
            },
        });
        setOpen(true);
    };

    const togglePid = (pid) => {
        setForm((f) => ({
            ...f,
            product_ids: f.product_ids.includes(pid)
                ? f.product_ids.filter((x) => x !== pid)
                : [...f.product_ids, pid],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.product_ids.length < 2) {
            toast.error("A bundle must contain at least 2 plugins.");
            return;
        }
        try {
            const payload = {
                ...form,
                price_clp: Number(form.price_clp) || 0,
                price_usd: Number(form.price_usd) || 0,
                sub_prices: {
                    monthly: { clp: Number(form.sub_prices.monthly.clp) || 0, usd: Number(form.sub_prices.monthly.usd) || 0 },
                    semiannual: { clp: Number(form.sub_prices.semiannual.clp) || 0, usd: Number(form.sub_prices.semiannual.usd) || 0 },
                    annual: { clp: Number(form.sub_prices.annual.clp) || 0, usd: Number(form.sub_prices.annual.usd) || 0 },
                },
            };
            if (form.sale_mode === "subscription") {
                const anyPrice = ["monthly", "semiannual", "annual"].some((k) => Number(form.sub_prices[k].usd) > 0 || Number(form.sub_prices[k].clp) > 0);
                if (!anyPrice) {
                    toast.error("Define al menos un precio de suscripción (CLP para Flow y/o USD para PayPal).");
                    return;
                }
            }
            if (editing) {
                await api.patch(`/admin/bundles/${editing.id}`, payload);
                toast.success("Bundle updated");
            } else {
                await api.post("/admin/bundles", payload);
                toast.success("Bundle created");
            }
            setOpen(false); setEditing(null); setForm(emptyBundle);
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    const remove = async (b) => {
        if (!window.confirm(`Delete bundle "${b.name}"?`)) return;
        try {
            await api.delete(`/admin/bundles/${b.id}`);
            toast.success("Bundle deleted");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    // ── price math preview ───────────────────────────────────────────
    const selectedProducts = products.filter((p) => form.product_ids.includes(p.id));
    const sumCLP = selectedProducts.reduce((acc, p) => acc + (Number(p.price_clp) || 0), 0);
    const sumUSD = selectedProducts.reduce((acc, p) => acc + (Number(p.price_usd) || 0), 0);
    const savingsCLP = Math.max(0, sumCLP - Number(form.price_clp || 0));
    const savingsUSD = Math.max(0, sumUSD - Number(form.price_usd || 0));
    const discountPct = sumCLP > 0 ? Math.round((savingsCLP / sumCLP) * 100) : 0;

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-bundles-page">
                <div>
                    <div className="label-mono mb-3">/ Bundles</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Bundles & deals</h1>
                    <p className="text-avx-muted mt-2 max-w-2xl">
                        Create packs by combining published plugins at a reduced price. Upon purchase, the customer receives an individual license for each plugin in the bundle.
                    </p>
                </div>
                <button onClick={openCreate} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="create-bundle-btn">
                    + Create bundle
                </button>
            </div>

            {bundles.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted">
                    No bundles yet. Create one by combining existing plugins at a reduced price to boost sales.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {bundles.map((b) => (
                        <div key={b.id} className="panel-elevated p-6 flex flex-col" data-testid={`bundle-card-${b.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <h3 className="font-heading font-bold text-xl mb-1">{b.name}</h3>
                                    <div className="label-mono">{b.products?.length || 0} plugins · {b.status}</div>
                                </div>
                                {b.badge_text && (
                                    <span className="px-2 py-1 rounded-sm bg-avx-blue/15 border border-avx-blue/40 text-avx-blue text-[10px] font-mono uppercase tracking-[0.2em]">
                                        {b.badge_text}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-avx-muted mb-3 leading-relaxed">{b.short_description}</p>

                            <div className="flex flex-wrap gap-1.5 mb-4">
                                {(b.products || []).map((p) => (
                                    <span key={p.id} className="px-2 py-0.5 rounded-sm bg-avx-bg/60 border border-avx-line text-[10px] font-mono">
                                        {p.name}
                                    </span>
                                ))}
                            </div>

                            <div className="flex items-end gap-3 mb-4">
                                <div>
                                    <div className="font-mono text-avx-blue font-bold text-lg">{formatCLP(b.price_clp)}</div>
                                    {b.original_price_clp > b.price_clp && (
                                        <div className="font-mono text-[10px] text-avx-muted line-through">{formatCLP(b.original_price_clp)}</div>
                                    )}
                                </div>
                                {b.discount_pct > 0 && (
                                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-avx-success bg-avx-success/15 px-2 py-1 rounded-sm border border-avx-success/40">
                                        −{b.discount_pct}%
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-2 pt-3 border-t border-avx-line mt-auto">
                                <button onClick={() => openEdit(b)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline" data-testid={`edit-bundle-${b.id}`}>Edit</button>
                                <button onClick={() => remove(b)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline ml-auto" data-testid={`delete-bundle-${b.id}`}>Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editing ? "Edit bundle" : "New bundle"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="bundle-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Name</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })} required data-testid="bundle-name" />
                            </div>
                            <div>
                                <Label className="label-mono">Slug</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} required data-testid="bundle-slug" />
                            </div>
                        </div>

                        <div>
                            <Label className="label-mono">Short description</Label>
                            <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.short_description}
                                onChange={(e) => setForm({ ...form, short_description: e.target.value })} placeholder="Highlighted text for the public card" data-testid="bundle-short-desc" />
                        </div>

                        <div>
                            <Label className="label-mono">Long description (optional)</Label>
                            <Textarea rows={3} className="mt-2 bg-avx-bg border-avx-line" value={form.full_description}
                                onChange={(e) => setForm({ ...form, full_description: e.target.value })} data-testid="bundle-full-desc" />
                        </div>

                        {/* Product picker */}
                        <div>
                            <Label className="label-mono">Included plugins · select at least 2</Label>
                            <div className="mt-2 panel p-3 max-h-64 overflow-y-auto space-y-2" data-testid="bundle-products-picker">
                                {products.length === 0 ? (
                                    <div className="text-avx-muted text-sm">No plugins created yet.</div>
                                ) : products.map((p) => {
                                    const checked = form.product_ids.includes(p.id);
                                    return (
                                        <label key={p.id} className={`flex items-center gap-3 p-2 rounded-sm border cursor-pointer transition-colors ${checked ? "bg-avx-blue/10 border-avx-blue/40" : "border-transparent hover:bg-avx-bg/40"}`}>
                                            <input type="checkbox" checked={checked} onChange={() => togglePid(p.id)} className="accent-avx-blue" data-testid={`bundle-pick-${p.id}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-mono">{p.name}</div>
                                                <div className="text-[10px] font-mono text-avx-muted uppercase tracking-[0.18em]">
                                                    {formatCLP(p.price_clp)} · {formatUSD(p.price_usd)} · {p.status}
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="mt-2 text-[10px] font-mono text-avx-muted uppercase tracking-[0.18em]">
                                Selected: {form.product_ids.length}
                                {selectedProducts.length > 0 && (
                                    <> · Original sum CLP {formatCLP(sumCLP)} · USD {formatUSD(sumUSD)}</>
                                )}
                            </div>
                        </div>

                        <div>
                            <Label className="label-mono">Modo de venta</Label>
                            <Select value={form.sale_mode} onValueChange={(v) => setForm({ ...form, sale_mode: v })}>
                                <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" data-testid="bundle-sale-mode"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="one_time">Pago único</SelectItem>
                                    <SelectItem value="subscription">Solo suscripción</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="text-[10px] font-mono text-avx-muted mt-1">
                                {form.sale_mode === "subscription"
                                    ? "El cliente paga de forma recurrente (mensual/semestral/anual). Las licencias se renuevan mientras la suscripción esté activa."
                                    : "El cliente paga una vez y conserva las licencias de forma permanente."}
                            </div>
                        </div>

                        {form.sale_mode === "one_time" ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Bundle price CLP</Label>
                                <Input type="number" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.price_clp}
                                    onChange={(e) => setForm({ ...form, price_clp: e.target.value })} placeholder="35000" data-testid="bundle-price-clp" />
                            </div>
                            <div>
                                <Label className="label-mono">Bundle price USD</Label>
                                <Input type="number" step="0.01" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.price_usd}
                                    onChange={(e) => setForm({ ...form, price_usd: e.target.value })} placeholder="38" data-testid="bundle-price-usd" />
                            </div>
                        </div>
                        ) : (
                        <div className="panel p-4 space-y-3" data-testid="bundle-sub-prices">
                            <div className="label-mono text-avx-blue">/ Precios de suscripción (CLP + USD)</div>
                            <div className="text-[10px] font-mono text-avx-muted">PayPal cobra en USD y Flow en CLP. Deja en 0 un precio/período que no quieras ofrecer en ese proveedor.</div>
                            {SUB_PERIODS.map((p) => (
                                <div key={p.key} className="grid grid-cols-[1fr_1fr_1fr] gap-3 items-end">
                                    <div className="text-sm font-mono self-center">{p.label}</div>
                                    <div>
                                        <Label className="label-mono text-[9px]">CLP</Label>
                                        <Input type="number" className="mt-1 bg-avx-bg border-avx-line h-10 font-mono" value={form.sub_prices[p.key].clp}
                                            onChange={(e) => setForm({ ...form, sub_prices: { ...form.sub_prices, [p.key]: { ...form.sub_prices[p.key], clp: e.target.value } } })}
                                            placeholder="9900" data-testid={`sub-price-${p.key}-clp`} />
                                    </div>
                                    <div>
                                        <Label className="label-mono text-[9px]">USD</Label>
                                        <Input type="number" step="0.01" className="mt-1 bg-avx-bg border-avx-line h-10 font-mono" value={form.sub_prices[p.key].usd}
                                            onChange={(e) => setForm({ ...form, sub_prices: { ...form.sub_prices, [p.key]: { ...form.sub_prices[p.key], usd: e.target.value } } })}
                                            placeholder="12" data-testid={`sub-price-${p.key}-usd`} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        )}

                        {form.sale_mode === "one_time" && selectedProducts.length >= 2 && discountPct > 0 && (
                            <div className="panel p-3 bg-avx-success/10 border-avx-success/40" data-testid="bundle-savings-preview">
                                <div className="label-mono mb-1 text-avx-success">/ Estimated savings</div>
                                <div className="text-sm font-mono">
                                    {formatCLP(savingsCLP)} · {formatUSD(savingsUSD)} · <span className="text-avx-success font-bold">−{discountPct}%</span>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="published">Publicado</SelectItem>
                                        <SelectItem value="draft">Draft (hidden)</SelectItem>
                                        <SelectItem value="coming_soon">Coming soon</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-mono">Badge text (optional)</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.badge_text}
                                    onChange={(e) => setForm({ ...form, badge_text: e.target.value })} placeholder="SALE · −30% · LAUNCH" data-testid="bundle-badge" />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-avx-line">
                            <button type="button" onClick={() => setOpen(false)} className="btn-secondary px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">Cancel</button>
                            <button type="submit" className="btn-primary px-6 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="bundle-submit">
                                {editing ? "Save changes" : "Create bundle"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
